<template>
  <div class="execution-results">
    <a-page-header title="评估结果" sub-title="查看执行批次和评估结果">
      <template #extra>
        <a-button @click="fetchBatches">
          <template #icon><ReloadOutlined /></template>
          刷新
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <!-- 筛选区域 -->
      <a-form layout="inline" style="margin-bottom: 16px">
        <a-form-item label="评估计划">
          <a-select 
            v-model:value="filters.test_plan_id" 
            style="width: 200px" 
            allow-clear
            placeholder="全部计划"
          >
            <a-select-option v-for="plan in testPlans" :key="plan.id" :value="plan.id">
              {{ plan.plan_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="状态">
          <a-select 
            v-model:value="filters.status" 
            style="width: 120px" 
            allow-clear
            placeholder="全部"
          >
            <a-select-option value="running">执行中</a-select-option>
            <a-select-option value="completed">已完成</a-select-option>
            <a-select-option value="failed">失败</a-select-option>
            <a-select-option value="cancelled">已取消</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="时间范围">
          <a-range-picker v-model:value="dateRange" style="width: 240px" />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="fetchBatches">查询</a-button>
          <a-button style="margin-left: 8px" @click="resetFilters">重置</a-button>
        </a-form-item>
      </a-form>

      <!-- 批量操作 -->
      <a-space style="margin-bottom: 16px" v-if="selectedRowKeys.length > 0">
        <a-button 
          type="primary" 
          @click="compareBatches"
          :disabled="selectedRowKeys.length !== 2"
        >
          对比选中批次 ({{ selectedRowKeys.length }}/2)
        </a-button>
      </a-space>

      <!-- 批次表格 -->
      <a-table
        :columns="columns"
        :data-source="batches"
        :loading="loading"
        :pagination="pagination"
        :row-selection="{ selectedRowKeys, onChange: onSelectChange }"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'batch_code'">
            <a @click="viewBatchDetail(record)">{{ record.batch_code }}</a>
          </template>
          <template v-else-if="column.key === 'plan_name'">
            <span>{{ record.plan_name || '-' }}</span>
            <a-tag v-if="record.eval_target" size="small" style="margin-left: 4px">
              {{ getEvalTargetLabel(record.eval_target) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'phase2_result'">
            <template v-if="record.phase2_status === 'completed'">
              <a-tag :color="getScoreLevelColor(record.phase2_score_level)">
                {{ record.phase2_score_level }} ({{ record.phase2_score_value }})
              </a-tag>
            </template>
            <template v-else-if="record.phase2_status === 'evaluating'">
              <a-tag color="processing">评估中...</a-tag>
            </template>
            <template v-else-if="record.phase2_status === 'failed'">
              <a-tag color="error">评估失败</a-tag>
            </template>
            <template v-else>
              <span>-</span>
            </template>
          </template>
          <template v-else-if="column.key === 'phase1_result'">
            <template v-if="record.phase1_avg_score !== null">
              <a-tag :color="getAvgScoreColor(record.phase1_avg_score)">
                {{ getAvgScoreLevel(record.phase1_avg_score) }} ({{ record.phase1_avg_score }})
              </a-tag>
              <a-tooltip v-if="record.phase1_score_distribution">
                <template #title>
                  A:{{ record.phase1_score_distribution?.A || 0 }}
                  B:{{ record.phase1_score_distribution?.B || 0 }}
                  C:{{ record.phase1_score_distribution?.C || 0 }}
                </template>
                <InfoCircleOutlined style="margin-left: 4px; color: #999" />
              </a-tooltip>
            </template>
            <template v-else>
              <span>-</span>
            </template>
          </template>
          <template v-else-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.status)">
              {{ getStatusText(record.status) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'create_time'">
            {{ formatTime(record.create_time) }}
          </template>
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="viewBatchDetail(record)">
                详情
              </a-button>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { message } from 'ant-design-vue';
import { ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons-vue';
import { useRouter, useRoute } from 'vue-router';
import { getPlanExecutions } from '@/api/evalPlanExecutionV2';
import { getTestPlans } from '@/api/evalTestPlanV2';
import dayjs from 'dayjs';

const router = useRouter();
const route = useRoute();

const batches = ref([]);
const testPlans = ref([]);
const loading = ref(false);
const selectedRowKeys = ref([]);
const dateRange = ref(null);

const filters = reactive({
  test_plan_id: route.query.test_plan_id ? Number(route.query.test_plan_id) : null,
  status: null
});

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0
});

const columns = [
  { title: '批次编号', dataIndex: 'batch_code', key: 'batch_code', width: 180 },
  { title: '评估计划', dataIndex: 'plan_name', key: 'plan_name', width: 220 },
  { title: '阶段2结果', dataIndex: 'phase2_result', key: 'phase2_result', width: 120 },
  { title: '阶段1平均', dataIndex: 'phase1_result', key: 'phase1_result', width: 140 },
  { title: '状态', dataIndex: 'status', key: 'status', width: 100 },
  { title: '时间', dataIndex: 'create_time', key: 'create_time', width: 170 },
  { title: '操作', key: 'action', width: 100, fixed: 'right' }
];

// 获取批次列表
const fetchBatches = async () => {
  loading.value = true;
  try {
    const params = {
      ...filters,
      page: pagination.current,
      pageSize: pagination.pageSize
    };
    
    if (dateRange.value && dateRange.value.length === 2) {
      params.start_date = dateRange.value[0].format('YYYY-MM-DD');
      params.end_date = dateRange.value[1].format('YYYY-MM-DD');
    }

    const res = await getPlanExecutions(params);
    if (res.code === 200) {
      batches.value = res.data?.list || [];
      pagination.total = res.data?.pagination?.total || 0;
    }
  } catch (error) {
    message.error('获取批次列表失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

// 获取评估计划列表（用于筛选）
const fetchTestPlans = async () => {
  try {
    const res = await getTestPlans({ pageSize: 100 });
    if (res.code === 200) {
      testPlans.value = res.data?.list || [];
    }
  } catch (error) {
    console.error('获取评估计划失败:', error);
  }
};

// 查看批次详情
const viewBatchDetail = (record) => {
  router.push(`/eval-v2/results/${record.id}`);
};

// 对比批次
const compareBatches = () => {
  if (selectedRowKeys.value.length !== 2) {
    message.warning('请选择2个批次进行对比');
    return;
  }
  router.push({
    path: '/eval-v2/results/compare',
    query: {
      batch1: selectedRowKeys.value[0],
      batch2: selectedRowKeys.value[1]
    }
  });
};

// 选择变化
const onSelectChange = (keys) => {
  if (keys.length > 2) {
    message.warning('最多选择2个批次进行对比');
    return;
  }
  selectedRowKeys.value = keys;
};

// 重置筛选
const resetFilters = () => {
  filters.test_plan_id = null;
  filters.status = null;
  dateRange.value = null;
  pagination.current = 1;
  fetchBatches();
};

// 表格变化
const handleTableChange = (pag) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  fetchBatches();
};

// 辅助函数
const getEvalTargetLabel = (target) => {
  const labels = {
    consistency: '遵从性',
    model_comparison: '模型对比',
    knowledge_structure: '知识结构',
    knowledge_content: '知识内容'
  };
  return labels[target] || target;
};

const getScoreLevelColor = (level) => {
  const colors = { A: 'success', B: 'processing', C: 'warning' };
  return colors[level] || 'default';
};

const getAvgScoreLevel = (score) => {
  if (score >= 90) return 'A';
  if (score >= 70) return 'B';
  return 'C';
};

const getAvgScoreColor = (score) => {
  if (score >= 90) return 'success';
  if (score >= 70) return 'processing';
  return 'warning';
};

const getStatusColor = (status) => {
  const colors = {
    running: 'processing',
    completed: 'success',
    failed: 'error',
    cancelled: 'default'
  };
  return colors[status] || 'default';
};

const getStatusText = (status) => {
  const texts = {
    running: '执行中',
    completed: '已完成',
    failed: '失败',
    cancelled: '已取消'
  };
  return texts[status] || status;
};

const formatTime = (time) => {
  if (!time) return '-';
  return dayjs(time).format('YYYY-MM-DD HH:mm');
};

onMounted(() => {
  fetchBatches();
  fetchTestPlans();
});
</script>

<style scoped>
.execution-results {
  padding: 24px;
}
</style>
